import CloseIcon from "@mui/icons-material/Close";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import LoadingButton from "../../../components/LoadingButton";
import { Company } from "../../../types";
import { hostname, parseError } from "../../../utils";
import { isFreeUser } from "../../../utils/subscriptionUtils";

interface TransferOwnershipDialogProps {
  open: boolean;
  onClose: () => void;
  company: Company;
  getToken: () => Promise<string>;
  eventListener: (event: any) => void;
  onSuccess: () => void;
  currentUser: any;
}

const TransferOwnershipDialog: React.FC<TransferOwnershipDialogProps> = ({
  open,
  onClose,
  company,
  getToken,
  eventListener,
  onSuccess,
  currentUser,
}) => {
  const [selectedMember, setSelectedMember] = useState<any>(null);
  const [members, setMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [transferring, setTransferring] = useState(false);
  const [targetUserSubscription, setTargetUserSubscription] =
    useState<string | null>(null);
  const [memberSearch, setMemberSearch] = useState("");
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  useEffect(() => {
    if (open) {
      loadCompanyMembers();
    }
  }, [open]);

  const loadCompanyMembers = async () => {
    try {
      setLoadingMembers(true);
      const currentToken = await getToken();
      const response = await axios.get(
        `${hostname}/company_users/${company.id}?per_page=100`,
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      // Filter to only show owners (eligible to receive ownership transfer)
      // and exclude the current user (they can't transfer ownership to themselves)
      const ownerMembers = response.data.company_users.filter(
        (member: any) => member.role === "owner" && member.email !== currentUser?.email
      );
      setMembers(ownerMembers);
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setLoadingMembers(false);
    }
  };

  const handleSelectMember = async (member: any) => {
    setSelectedMember(member);
    setMemberSearch(member.email);
    setTargetUserSubscription(null); // Reset subscription while loading
    setLoadingSubscription(true);

    // Fetch their subscription type
    try {
      const currentToken = await getToken();
      const response = await axios.get(
        `${hostname}/company_users/${member.id}/subscription-type`,
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );
      setTargetUserSubscription(response.data.subscriptionType);
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
      setTargetUserSubscription(null);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleTransfer = async () => {
    if (!selectedMember) return;

    setTransferring(true);

    try {
      const currentToken = await getToken();
      await axios.put(
        `${hostname}/companies/${company.id}/transfer-ownership`,
        {
          newOwnerId: selectedMember.id,
        },
        {
          headers: { Authorization: `Bearer ${currentToken}` },
        }
      );

      eventListener({
        type: "SET_INFO",
        info: "Ownership transferred successfully",
      });

      onSuccess();
    } catch (err: any) {
      eventListener({
        type: "SET_ERROR",
        error: parseError(err),
      });
    } finally {
      setTransferring(false);
    }
  };

  const isFree = selectedMember && targetUserSubscription && isFreeUser(targetUserSubscription);
  const filteredMembers = members.filter((m) =>
    m.email.toLowerCase().includes(memberSearch.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Transfer Ownership - {company.name}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {isFree && selectedMember && (
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mt: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Warning: {selectedMember.email} is on a free tier license.
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Once you transfer ownership to this user, your company features
                will no longer be available until they upgrade to a paid plan.
              </Typography>
            </Alert>
          )}

          <Typography variant="body2" sx={{ mt: 2 }}>
            Select an owner to transfer ownership to:
          </Typography>

          <TextField
            placeholder="Search members..."
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            fullWidth
            size="small"
            disabled={loadingMembers}
          />

          {loadingMembers ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={30} />
            </Box>
          ) : filteredMembers.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              {memberSearch
                ? "No owners found matching your search"
                : "No other owners available"}
            </Typography>
          ) : (
            <Box
              sx={{
                border: "1px solid #ccc",
                borderRadius: 1,
                maxHeight: 300,
                overflow: "auto",
              }}
            >
              {filteredMembers.map((member) => (
                <Box
                  key={member.id}
                  sx={{
                    p: 2,
                    borderBottom: "1px solid #eee",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSelectMember(member)}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: selectedMember?.id === member.id ? 600 : 400,
                    }}
                  >
                    {member.displayName || member.email}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {member.email}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}

          {selectedMember && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Subscription: <strong>{targetUserSubscription}</strong>
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          color={isFree ? "warning" : "primary"}
          onClick={handleTransfer}
          loading={transferring}
          disabled={!selectedMember}
        >
          {isFree ? "Transfer Anyway" : "Transfer Ownership"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default TransferOwnershipDialog;
